import { makeAutoObservable, runInAction } from "mobx";
import AlertsAPI, { AlertsApiError } from "../../api/AlertsAPI";
import {
  ALERT_BULK_MAX_IDS,
  AlertAdminRef,
  AlertDetail,
  AlertErrorCode,
  AlertItem,
  AlertMeta,
  AlertMetaRefType,
  AlertMetaSection,
  AlertSectionSummary,
  AlertSummary,
  isAlertOpen,
  isAlertUnread,
} from "../../../../models/Alerts/Alert";
import { AlertListModel } from "./AlertListModel";
import {
  readSharedSummary,
  subscribeSharedSummary,
  withPollLock,
  writeSharedSummary,
} from "./alertsSummarySync";

const POLL_INTERVAL_MS = 30_000;
const POLL_BACKOFF_MS = [120_000, 240_000];
/** A summary another tab fetched within this window is reused, not re-fetched. */
const SHARED_FRESH_MS = POLL_INTERVAL_MS - 5_000;
const META_TTL_MS = 60 * 60 * 1000;
/** Summary is read from a replica; give it a moment before reconciling. */
const RECONCILE_DELAY_MS = 1_500;
const DELTA_LIMIT = 100;

export interface AlertSectionView extends AlertMetaSection {
  unread: number;
  high_unread: number;
  open: number;
}

export interface ResolveConflict {
  resolvedBy: AlertAdminRef | null;
  resolvedAt: string | null;
}

const emptySection = (section: string): AlertSectionSummary => ({
  section,
  unread: 0,
  high_unread: 0,
  open: 0,
});

/**
 * Alerts state shared by the navbar bell, the inbox page and the detail page.
 * A single instance lives for the whole workspace session (switching
 * workspace reloads the page), so the badge and lists stay in sync.
 */
export class AlertsStore {
  private api = new AlertsAPI();
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private reconcileTimer: ReturnType<typeof setTimeout> | null = null;
  private pollFailures = 0;
  private isPollingActive = false;
  private metaLoadedAt = 0;
  /** When the summary we hold was fetched (by this tab or another). */
  private summaryFetchedAt = 0;
  /**
   * Cursor the loaded lists were last synced to, while a hidden tab defers
   * the delta fetch for summaries other tabs published. `undefined` = none.
   */
  private pendingSyncFrom: string | null | undefined = undefined;
  private unsubscribeShared: (() => void) | null = null;
  /**
   * Set from a local action until its forced poll lands. Summaries from
   * other tabs predate the action, so adopting one would undo the
   * optimistic counts.
   */
  private localChangePending = false;

  summary: AlertSummary | null = null;
  summaryError = "";
  meta: AlertMeta | null = null;
  metaError = "";

  panel: AlertListModel;
  inbox: AlertListModel;

  detail: AlertDetail | null = null;
  isDetailLoading = false;
  detailError = "";
  detailNotFound = false;

  isResolving = false;
  resolveError = "";
  resolveConflict: ResolveConflict | null = null;
  resolveErrorCode: AlertErrorCode | null = null;

  constructor() {
    this.panel = new AlertListModel(this.api, "INBOX", 15);
    this.inbox = new AlertListModel(this.api, "INBOX", 30);
    makeAutoObservable<
      AlertsStore,
      | "api"
      | "pollTimer"
      | "reconcileTimer"
      | "pollFailures"
      | "isPollingActive"
      | "metaLoadedAt"
      | "summaryFetchedAt"
      | "pendingSyncFrom"
      | "unsubscribeShared"
      | "localChangePending"
    >(this, {
      api: false,
      pollTimer: false,
      reconcileTimer: false,
      pollFailures: false,
      isPollingActive: false,
      metaLoadedAt: false,
      summaryFetchedAt: false,
      pendingSyncFrom: false,
      unsubscribeShared: false,
      localChangePending: false,
    });
  }

  // ---------------------------------------------------------------- derived

  get totalUnread(): number {
    return this.summary?.total_unread ?? 0;
  }

  get totalHighUnread(): number {
    return this.summary?.total_high_unread ?? 0;
  }

  /** Meta sections in display order, joined with their badge counts. */
  get sections(): AlertSectionView[] {
    const counts = new Map(
      (this.summary?.sections ?? []).map((row) => [row.section, row]),
    );
    const metaSections = [...(this.meta?.sections ?? [])].sort(
      (a, b) => a.sort_order - b.sort_order,
    );
    const known = new Set(metaSections.map((section) => section.code));
    const extra: AlertMetaSection[] = Array.from(counts.keys())
      .filter((code) => !known.has(code))
      .map((code, index) => ({
        code,
        label: code,
        icon: null,
        sort_order: 1000 + index,
      }));

    return [...metaSections, ...extra].map((section) => {
      const row = counts.get(section.code) ?? emptySection(section.code);
      return {
        ...section,
        unread: row.unread,
        high_unread: row.high_unread,
        open: row.open,
      };
    });
  }

  sectionLabel(code: string): string {
    return (
      this.meta?.sections.find((section) => section.code === code)?.label ??
      code
    );
  }

  sectionIcon(code: string): string | null {
    return (
      this.meta?.sections.find((section) => section.code === code)?.icon ?? null
    );
  }

  refType(code: string): AlertMetaRefType | undefined {
    return this.meta?.ref_types.find((refType) => refType.code === code);
  }

  /** In-app route for the alert's record, or null when the ref type is unknown. */
  webRoute(alert: Pick<AlertItem, "ref">): string | null {
    const refType = this.refType(alert.ref.type);
    if (!refType?.web_route || !refType.web_route.includes("{ref_id}")) {
      return null;
    }
    return refType.web_route.replace(
      /\{ref_id\}/g,
      encodeURIComponent(alert.ref.id),
    );
  }

  // ---------------------------------------------------------------- polling

  startPolling() {
    if (this.isPollingActive) return;
    this.isPollingActive = true;
    document.addEventListener("visibilitychange", this.handleVisibility);
    this.unsubscribeShared = subscribeSharedSummary((entry) => {
      if (this.localChangePending) return;
      if (entry.fetchedAt <= this.summaryFetchedAt) return;
      this.applySummary(entry.summary, entry.fetchedAt).finally(() =>
        this.scheduleNextPoll(),
      );
    });
    this.loadMeta();
    this.pollNow();
  }

  stopPolling() {
    this.isPollingActive = false;
    document.removeEventListener("visibilitychange", this.handleVisibility);
    this.unsubscribeShared?.();
    this.unsubscribeShared = null;
    this.clearPollTimer();
    if (this.reconcileTimer) clearTimeout(this.reconcileTimer);
    this.reconcileTimer = null;
    this.localChangePending = false;
  }

  private handleVisibility = () => {
    if (document.visibilityState === "hidden") {
      this.clearPollTimer();
    } else {
      this.pollNow();
    }
  };

  private clearPollTimer() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  private scheduleNextPoll() {
    this.clearPollTimer();
    if (!this.isPollingActive || document.visibilityState === "hidden") return;
    // Count from the last fetch by any tab, so a summary another tab just
    // published pushes this tab's next poll back.
    const delay =
      this.pollFailures === 0
        ? Math.max(0, this.summaryFetchedAt + POLL_INTERVAL_MS - Date.now())
        : POLL_BACKOFF_MS[
            Math.min(this.pollFailures, POLL_BACKOFF_MS.length) - 1
          ];
    this.pollTimer = setTimeout(() => this.pollNow(), delay);
  }

  /** Polls soon after a local action, once the replica has caught up. */
  private scheduleReconcile() {
    this.localChangePending = true;
    if (this.reconcileTimer) clearTimeout(this.reconcileTimer);
    this.reconcileTimer = setTimeout(() => {
      this.reconcileTimer = null;
      this.pollNow(true);
    }, RECONCILE_DELAY_MS);
  }

  /**
   * Refreshes the summary, reusing one another tab fetched recently unless
   * `force` (after a local action, where a pre-action summary is stale).
   */
  async pollNow(force = false) {
    // The pending reconcile poll will refresh; don't adopt a stale summary.
    if (!force && this.localChangePending) return;
    this.clearPollTimer();
    try {
      if (!force && (await this.adoptFreshShared())) return;
      await withPollLock(force, async () => {
        // Another tab may have fetched while we waited for the lock.
        if (!force && (await this.adoptFreshShared())) return;
        const summary = await this.api.getSummary();
        const fetchedAt = Date.now();
        writeSharedSummary({ summary, fetchedAt });
        // A newer local action re-armed the reconcile; its poll owns the
        // counts now, so keep the optimistic state until it lands.
        if (force && this.reconcileTimer) return;
        this.localChangePending = false;
        await this.applySummary(summary, fetchedAt);
      });
    } catch (error: any) {
      this.pollFailures += 1;
      // Don't block shared summaries forever if the reconcile poll failed.
      if (force && !this.reconcileTimer) this.localChangePending = false;
      runInAction(() => {
        this.summaryError = error?.message || "Failed to load alerts.";
      });
    } finally {
      this.scheduleNextPoll();
    }
  }

  private async adoptFreshShared(): Promise<boolean> {
    const shared = readSharedSummary();
    if (!shared || Date.now() - shared.fetchedAt >= SHARED_FRESH_MS) {
      return false;
    }
    if (shared.fetchedAt > this.summaryFetchedAt || this.summary == null) {
      await this.applySummary(shared.summary, shared.fetchedAt);
    } else if (this.pendingSyncFrom !== undefined) {
      // Already holding it, but a delta deferred while hidden is still owed.
      await this.applySummary(this.summary, this.summaryFetchedAt);
    }
    return true;
  }

  private async applySummary(summary: AlertSummary, fetchedAt: number) {
    const hadSummary = this.summary != null;
    const previousCursor =
      this.pendingSyncFrom !== undefined
        ? this.pendingSyncFrom
        : this.summary?.cursor ?? null;
    this.summaryFetchedAt = fetchedAt;
    this.pollFailures = 0;
    runInAction(() => {
      this.summary = summary;
      this.summaryError = "";
    });
    if (!hadSummary || summary.cursor === previousCursor) {
      this.pendingSyncFrom = undefined;
      return;
    }
    // Hidden tabs only take the badge counts; the list delta waits until
    // the tab is visible again so N background tabs don't each fetch it.
    if (document.visibilityState === "hidden") {
      this.pendingSyncFrom = previousCursor;
      return;
    }
    this.pendingSyncFrom = undefined;
    await this.syncChanges(previousCursor, summary.cursor);
  }

  /** Merges rows changed since the previous cursor into any loaded list. */
  private async syncChanges(
    previousCursor: string | null,
    nextCursor: string | null,
  ) {
    const movedBackwards =
      previousCursor != null &&
      nextCursor != null &&
      new Date(nextCursor).getTime() < new Date(previousCursor).getTime();

    if (previousCursor == null || nextCursor == null || movedBackwards) {
      this.reloadLoadedLists();
      return;
    }

    try {
      const delta = await this.api.listAlerts({
        since: previousCursor,
        status: "ALL",
        archived: "all",
        page_no: 1,
        limit: DELTA_LIMIT,
      });
      if (delta.total_count > delta.list.length) {
        this.reloadLoadedLists();
        return;
      }
      runInAction(() => this.applyRows(delta.list));
    } catch (error) {
      console.error(error);
    }
  }

  private reloadLoadedLists() {
    [this.panel, this.inbox].forEach((list) => {
      if (list.hasLoaded) list.load();
    });
  }

  async loadMeta(force = false) {
    if (!force && this.meta && Date.now() - this.metaLoadedAt < META_TTL_MS) {
      return;
    }
    try {
      const meta = await this.api.getMeta();
      this.metaLoadedAt = Date.now();
      runInAction(() => {
        this.meta = meta;
        this.metaError = "";
      });
    } catch (error: any) {
      runInAction(() => {
        this.metaError = error?.message || "Failed to load alert setup.";
      });
    }
  }

  // ---------------------------------------------------------------- local state

  private knownItem(id: number): AlertItem | undefined {
    if (this.detail?.id === id) return this.detail;
    return this.inbox.findItem(id) ?? this.panel.findItem(id);
  }

  /** Puts updated rows into every list and the open detail. */
  private applyRows(rows: AlertItem[]) {
    if (rows.length === 0) return;
    this.panel.merge(rows);
    this.inbox.merge(rows);
    const detailRow = this.detail
      ? rows.find((row) => row.id === this.detail?.id)
      : undefined;
    if (this.detail && detailRow) {
      this.detail = { ...this.detail, ...detailRow };
    }
  }

  /** Adjusts badge counts for one row's before → after change. */
  private adjustSummary(before: AlertItem, after: AlertItem) {
    if (!this.summary) return;
    const unreadDelta =
      Number(isAlertUnread(after)) - Number(isAlertUnread(before));
    const highDelta =
      after.severity === "HIGH" || before.severity === "HIGH"
        ? Number(isAlertUnread(after) && after.severity === "HIGH") -
          Number(isAlertUnread(before) && before.severity === "HIGH")
        : 0;
    const openDelta = Number(isAlertOpen(after)) - Number(isAlertOpen(before));
    if (!unreadDelta && !highDelta && !openDelta) return;

    const sections = [...this.summary.sections];
    const index = sections.findIndex((row) => row.section === after.section);
    const row = index >= 0 ? sections[index] : emptySection(after.section);
    const nextRow = {
      ...row,
      unread: Math.max(0, row.unread + unreadDelta),
      high_unread: Math.max(0, row.high_unread + highDelta),
      open: Math.max(0, row.open + openDelta),
    };
    if (index >= 0) sections[index] = nextRow;
    else sections.push(nextRow);

    this.summary = {
      ...this.summary,
      total_unread: Math.max(0, this.summary.total_unread + unreadDelta),
      total_high_unread: Math.max(
        0,
        this.summary.total_high_unread + highDelta,
      ),
      sections,
    };
  }

  private applyLocal(ids: number[], update: (item: AlertItem) => AlertItem) {
    const rows: AlertItem[] = [];
    ids.forEach((id) => {
      const before = this.knownItem(id);
      if (!before) return;
      const after = update(before);
      this.adjustSummary(before, after);
      rows.push(after);
    });
    this.applyRows(rows);
  }

  // ---------------------------------------------------------------- actions

  async markRead(ids: number[]) {
    const unique = Array.from(new Set(ids)).slice(0, ALERT_BULK_MAX_IDS);
    if (unique.length === 0) return;
    const now = new Date().toISOString();
    this.applyLocal(unique, (item) =>
      item.me.read_at ? item : { ...item, me: { ...item.me, read_at: now } },
    );
    try {
      await this.api.markRead(unique);
    } finally {
      this.scheduleReconcile();
    }
  }

  async markAllRead(section?: string | null) {
    await this.api.markAllRead(section ?? undefined);
    runInAction(() => {
      const now = new Date().toISOString();
      const rows = [...this.panel.items, ...this.inbox.items]
        .filter((item) => !section || item.section === section)
        .filter((item) => item.me.read_at == null)
        .map((item) => ({ ...item, me: { ...item.me, read_at: now } }));
      this.applyRows(rows);
      if (this.detail && (!section || this.detail.section === section)) {
        this.detail = {
          ...this.detail,
          me: { ...this.detail.me, read_at: this.detail.me.read_at ?? now },
        };
      }

      if (this.summary) {
        const sections = this.summary.sections.map((row) =>
          !section || row.section === section
            ? { ...row, unread: 0, high_unread: 0 }
            : row,
        );
        const target = this.summary.sections.find(
          (row) => row.section === section,
        );
        this.summary = {
          ...this.summary,
          total_unread: section
            ? Math.max(0, this.summary.total_unread - (target?.unread ?? 0))
            : 0,
          total_high_unread: section
            ? Math.max(
                0,
                this.summary.total_high_unread - (target?.high_unread ?? 0),
              )
            : 0,
          sections,
        };
      }
    });
    this.scheduleReconcile();
  }

  async archive(ids: number[]) {
    const unique = Array.from(new Set(ids)).slice(0, ALERT_BULK_MAX_IDS);
    if (unique.length === 0) return;
    await this.api.archive(unique);
    runInAction(() => {
      const now = new Date().toISOString();
      this.applyLocal(unique, (item) => ({
        ...item,
        me: {
          ...item.me,
          archived_at: item.me.archived_at ?? now,
          read_at: item.me.read_at ?? now,
        },
      }));
    });
    this.scheduleReconcile();
  }

  async unarchive(ids: number[]) {
    const unique = Array.from(new Set(ids)).slice(0, ALERT_BULK_MAX_IDS);
    if (unique.length === 0) return;
    await this.api.unarchive(unique);
    runInAction(() => {
      this.applyLocal(unique, (item) => ({
        ...item,
        me: { ...item.me, archived_at: null },
      }));
    });
    this.scheduleReconcile();
  }

  clearResolveState() {
    this.resolveError = "";
    this.resolveConflict = null;
    this.resolveErrorCode = null;
  }

  /** Returns true when resolved; on failure the reason is in resolveError. */
  async resolve(alertId: number, note: string): Promise<boolean> {
    this.isResolving = true;
    this.clearResolveState();
    try {
      const updated = await this.api.resolve(alertId, note.trim());
      runInAction(() => {
        const before = this.knownItem(alertId);
        if (before) this.adjustSummary(before, updated);
        this.applyRows([updated]);
        if (this.detail?.id === alertId) this.refreshDetail();
      });
      this.scheduleReconcile();
      return true;
    } catch (error) {
      runInAction(() => {
        const apiError = error as AlertsApiError;
        this.resolveErrorCode = apiError?.code ?? null;
        if (apiError?.code === "ALREADY_RESOLVED") {
          const details = apiError.details ?? {};
          const resolvedBy = (details.resolved_by as AlertAdminRef) ?? null;
          this.resolveConflict = {
            resolvedBy,
            resolvedAt: (details.resolved_at as string) ?? null,
          };
          this.resolveError = `Already resolved by ${
            resolvedBy?.name ?? "another admin"
          }.`;
        } else if (apiError?.code === "NOT_RESOLVABLE") {
          this.resolveError =
            "This alert is informational and can't be resolved.";
        } else {
          this.resolveError = apiError?.message || "Failed to resolve alert.";
        }
      });
      if ((error as AlertsApiError)?.code === "ALREADY_RESOLVED") {
        this.reloadLoadedLists();
        if (this.detail?.id === alertId) this.refreshDetail();
        this.scheduleReconcile();
      }
      return false;
    } finally {
      runInAction(() => {
        this.isResolving = false;
      });
    }
  }

  // ---------------------------------------------------------------- detail

  async loadDetail(alertId: number) {
    this.isDetailLoading = true;
    this.detailError = "";
    this.detailNotFound = false;
    if (this.detail?.id !== alertId) this.detail = null;
    try {
      const detail = await this.api.getAlert(alertId);
      runInAction(() => {
        this.detail = detail;
      });
      if (isAlertUnread(detail)) {
        this.markRead([alertId]).catch((error) => console.error(error));
      }
    } catch (error) {
      runInAction(() => {
        const apiError = error as AlertsApiError;
        this.detailNotFound =
          apiError?.code === "NOT_FOUND" || apiError?.status === 404;
        this.detailError = this.detailNotFound
          ? "This alert doesn't exist or wasn't sent to you."
          : apiError?.message || "Failed to load alert.";
      });
    } finally {
      runInAction(() => {
        this.isDetailLoading = false;
      });
    }
  }

  private async refreshDetail() {
    if (!this.detail) return;
    try {
      const detail = await this.api.getAlert(this.detail.id);
      runInAction(() => {
        this.detail = detail;
      });
    } catch (error) {
      console.error(error);
    }
  }

  clearDetail() {
    this.detail = null;
    this.detailError = "";
    this.detailNotFound = false;
    this.clearResolveState();
  }
}

export const alertsStore = new AlertsStore();
